import isBrowser from '../../src/quince-site-common/src/utils/isBrowser'

import { DEFAULT_LANGUAGE, IS_I18N_ENABLED, STORAGE_PREFIX } from '../constants/storage'

/**
 * Storage options for managing localized and global session data
 * @property isGlobal - Determines how the key is stored and accessed:
 *   - true: The data will be shared across all languages/locales
 *   - false: The data will be specific to the current language/locale
 */
interface SessionStorageOptions {
  isGlobal: boolean
}

/**
 * Enhanced session storage class that handles both global and localized data storage
 * with automatic migration from legacy formats.
 * 
 * Key Format:
 * - Global keys: quince:global:keyName
 * - Localized keys: quince:{language}:keyName (e.g., quince:en-US:keyName)
 */
class EnhancedSessionStorage {
  private readonly PREFIX = STORAGE_PREFIX

  private getKeyWithPrefix(key: string, options: SessionStorageOptions): string {
    // If i18n is disabled, use original key without any prefix
    if (!IS_I18N_ENABLED) {
      return key
    }
    // If global, use legacy format with just storage prefix
    if (options.isGlobal) {
      return `${this.PREFIX}:${key}`
    }
    // Use full i18n format
    return `${this.PREFIX}:${DEFAULT_LANGUAGE}:${key}`
  }

  private getLegacyKey(key: string): string {
    // If i18n is disabled, use original key
    if (!IS_I18N_ENABLED) {
      return key
    }
    return `${this.PREFIX}:${key}`
  }

  /**
   * Retrieves an item from session storage with proper localization handling
   * @param key - The key to retrieve
   * @param options - Storage options
   * @returns The stored value or null if not found
   */
  getItem<T>(key: string, options: SessionStorageOptions): T | null {
    if (!isBrowser()) return null
    try {
      // If i18n is disabled, only try the original key
      if (!IS_I18N_ENABLED) {
        const item = sessionStorage.getItem(key)
        return item ? JSON.parse(item) as T : null
      }

      // Try getting with new prefix
      const prefixedKey = this.getKeyWithPrefix(key, options)
      let item = sessionStorage.getItem(prefixedKey)

      if (!item) {
        // Try legacy key format
        const legacyKey = this.getLegacyKey(key)
        item = sessionStorage.getItem(legacyKey)

        if (!item) {
          // Try original key
          item = sessionStorage.getItem(key)
        }

        // If found in legacy/original format and i18n is enabled, migrate to new format
        if (item && !options.isGlobal) {
          sessionStorage.setItem(prefixedKey, item)
          sessionStorage.removeItem(legacyKey)
          sessionStorage.removeItem(key)
        }
      }

      return item ? JSON.parse(item) as T : null
    } catch (error) {
      console.error('Error getting item from session storage:', error)
      return null
    }
  }

  /**
   * Stores an item with proper localization handling
   * @param key - The key to store
   * @param value - The value to store
   * @param options - Storage options
   */
  setItem<T>(key: string, value: T, options: SessionStorageOptions): void {
    if (!isBrowser()) return
    try {
      const serializedValue = JSON.stringify(value)

      // If i18n is disabled, only use original key
      if (!IS_I18N_ENABLED) {
        sessionStorage.setItem(key, serializedValue)
        return
      }

      const prefixedKey = this.getKeyWithPrefix(key, options)
      sessionStorage.setItem(prefixedKey, serializedValue)

      // For global keys, also maintain legacy format
      if (options.isGlobal) {
        sessionStorage.setItem(this.getLegacyKey(key), serializedValue)
        sessionStorage.setItem(key, serializedValue)
      }
    } catch (error) {
      console.error('Error setting item in session storage:', error)
    }
  }

  /**
   * Removes an item from session storage with proper localization handling
   * @param key - The key to remove
   * @param options - Storage options
   */
  removeItem(key: string, options: SessionStorageOptions): void {
    if (!isBrowser()) return
    try {
      // If i18n is disabled, only remove original key
      if (!IS_I18N_ENABLED) {
        sessionStorage.removeItem(key)
        return
      }

      const prefixedKey = this.getKeyWithPrefix(key, options)
      sessionStorage.removeItem(prefixedKey)

      // For global keys, also remove legacy formats
      if (options.isGlobal) {
        sessionStorage.removeItem(this.getLegacyKey(key))
        sessionStorage.removeItem(key)
      }
    } catch (error) {
      console.error('Error removing item from session storage:', error)
    }
  }

  /**
   * Checks if an item exists in session storage
   * @param key - The key to check
   * @param options - Storage options
   * @returns true if the item exists in any format, false otherwise
   */
  hasItem(key: string, options: SessionStorageOptions): boolean {
    if (!isBrowser()) return false

    // If i18n is disabled, only check original key
    if (!IS_I18N_ENABLED) {
      return !!sessionStorage.getItem(key)
    }
    
    const prefixedKey = this.getKeyWithPrefix(key, options)
    const legacyKey = this.getLegacyKey(key)
    
    return !!(
      sessionStorage.getItem(prefixedKey) ??
      sessionStorage.getItem(legacyKey) ??
      sessionStorage.getItem(key)
    )
  }
}

// Create a singleton instance
let sessionStorageInstance: EnhancedSessionStorage | null = null

export const getSessionStorage = (): EnhancedSessionStorage => {
  sessionStorageInstance ??= new EnhancedSessionStorage()
  return sessionStorageInstance
}